/* Native scene animation and synchronized media, isolated from the hero renderer. */
(() => {
  'use strict';
  const instances = new Map();
  const clamp = n => Math.max(0, Math.min(1, n));
  const ramp = (a,b,t) => {const p=clamp((t-a)/(b-a));return p*p*(3-2*p);};
  function lyricsFromLRC(raw) {
    const rows=[]; const offset=Number(raw.match(/\[offset:([+-]?\d+)\]/i)?.[1]||0)/1000;
    for(const line of raw.split(/\r?\n/)) {
      const text=line.replace(/\[[^\]]*\]/g,'').trim();
      if(!text)continue;
      for(const stamp of line.matchAll(/\[(\d+):(\d{2})(?:[.:](\d{1,3}))?\]/g))rows.push({time:Number(stamp[1])*60+Number(stamp[2])+Number('0.'+(stamp[3]||'0'))-offset,text});
    }
    return rows.sort((a,b)=>a.time-b.time);
  }
  function mount(root) {
    if(instances.has(root))return;
    const controller=new AbortController(),{signal}=controller;
    const reduced=matchMedia('(prefers-reduced-motion: reduce)');
    const media=root.querySelector('.feature-media'),videos=[...root.querySelectorAll('[data-exp-video]')];
    const videoButton=root.querySelector('.mxe-video-play'),aiButton=root.querySelector('.mxe-ai-play');
    const audio=root.querySelector('.mxe-audio'),audioButton=root.querySelector('.mxe-audio-play'),lyricButton=root.querySelector('.mxe-lyrics-play');
    const screens=[...root.querySelectorAll('.mxe-ai-screen')],lyrics=root.querySelector('.mxe-lyrics');
    const musicScreen=root.querySelector('.mxe-music-screen'),musicToggle=root.querySelector('.mxe-music-toggle');
    let lyricsOpen=false;
    let cues=[],timedLyrics=false;
    try{
      const raw=JSON.parse(root.querySelector('[data-exp-lyrics]')?.textContent||'""');
      if(typeof raw==='string'){
        cues=lyricsFromLRC(raw);timedLyrics=cues.length>0;
        if(!timedLyrics)cues=raw.split(/\r?\n/).map(text=>text.trim()).filter(text=>text&&!/^\[[^\]]*\]$/.test(text)).map((text,i)=>({time:i*.9,text}));
      }
    }catch{}
    let visible=false,alive=true,raf=0,last=0,elapsed=0,lyricIndex=-2,lyricElapsed=0,lyricMotion=null,lyricWanted=!reduced.matches,videoWanted=!reduced.matches,aiWanted=!reduced.matches,videoStarting=false,syncTick=0,aiTick=0;
    const lyricTrack=document.createElement('div');lyricTrack.className='mxe-lyrics-track';
    if(lyrics&&cues.length){
      // End copies keep the fast silent preview moving upward across its loop.
      [...cues.map((_,i)=>i),...cues.map((_,i)=>i)].forEach((index,position)=>{
        const p=document.createElement('p');p.textContent=cues[index].text;p.dataset.lyric=String(index);
        if(position>=cues.length)p.setAttribute('aria-hidden','true');
        lyricTrack.append(p);
      });
      lyrics.replaceChildren(lyricTrack);
    }else if(lyricButton)lyricButton.hidden=true;
    const currentScene=()=>root.querySelector('.feature-media-layer.is-visible .mxe-scene')?.dataset.scene;
    const available=()=>alive&&visible&&!document.hidden;
    function resize(){
      media.style.setProperty('--mxe-scale',media.clientWidth/756);
      const music=root.querySelector('.mxe-music-screen'),install=root.querySelector('.mxe-install-screen');
      // Corners measured from each source photo: TL, TR, BR, BL. Use both axes
      // independently, then reserve the same physical bezel in screen coordinates.
      if(music)fitScreen(music,[[136.902144,147.856309],[414.661641,24.065533],[353.983506,325.629218],[69.935257,445.684693]],503,747);
      if(install)fitScreen(install,[[887.126623,357.481664],[1186.225391,263.290330],[1127.170135,582.390874],[823.961017,673.405461]],2428,1798);
    }
    const ro=new ResizeObserver(resize);ro.observe(media);resize();
    function button(button,wanted,label){if(!button)return;button.textContent=(wanted?'Pause ':'Play ')+label;button.setAttribute('aria-label',button.textContent);button.setAttribute('aria-pressed',String(wanted));}
    function pauseVideos(){videos.forEach(v=>v.pause());}
    async function startVideos(){
      if(videoStarting||!videos.length)return;videoStarting=true;
      videos.forEach(v=>{if(v.readyState<2&&v.preload==='none'){v.preload='auto';v.load();}});
      if(videos.some(v=>v.readyState<2)){videoStarting=false;return;}
      if(Number.isFinite(videos[0].duration))videos.slice(1).forEach(v=>{if(Math.abs(v.currentTime-videos[0].currentTime)>.04)v.currentTime=videos[0].currentTime;});
      const results=await Promise.allSettled(videos.map(v=>v.play()));videoStarting=false;
      if(!available()||currentScene()!=='topic_4'||!videoWanted){pauseVideos();return;}
      if(results.some(r=>r.status==='rejected')){pauseVideos();videoWanted=false;button(videoButton,false,'videos');}
    }
    function renderLyrics(force=false){
      if(!lyrics||!cues.length)return;
      let index=-1;
      if(audio&&timedLyrics){for(let i=0;i<cues.length&&cues[i].time<=audio.currentTime;i++)index=i;}
      else index=Math.floor((audio?audio.currentTime:lyricElapsed)/.9)%cues.length;
      if(index===lyricIndex&&!force)return;
      const previous=lyricIndex;lyricIndex=index;lyrics.hidden=index<0||!lyricsOpen;
      const from=getComputedStyle(lyricTrack).transform;lyricMotion?.cancel();lyricMotion=null;
      if(index<0)return;
      [...lyricTrack.children].forEach(p=>p.classList.toggle('is-current',Number(p.dataset.lyric)===index));
      const wrap=previous===cues.length-1&&index===0&&cues.length>1;
      const node=lyricTrack.children[wrap?cues.length:index];
      const position=p=>`translateY(${-p.offsetTop}px)`;
      lyricTrack.style.transform=position(node);
      if(!force&&!reduced.matches&&previous>=0&&(index===previous+1||wrap)){
        const motion=lyricTrack.animate([{transform:from},{transform:lyricTrack.style.transform}],{duration:180,easing:'linear'});
        lyricMotion=motion;
        motion.finished.then(()=>{
          if(lyricMotion!==motion)return;
          if(wrap)lyricTrack.style.transform=position(lyricTrack.children[0]);
          lyricMotion=null;
        }).catch(()=>{});
      }else if(wrap)lyricTrack.style.transform=position(lyricTrack.children[0]);
    }
    function drawAI(t){
      const completed=t>=15,states=['Reviewing the workspace','Checking the updated interface','Running the final tests','Preparing the result'];
      for(const ui of screens){
        ui.querySelector('[data-agent-status]').textContent=completed?'Completed':t>10?'Checking':'Running';
        ui.querySelector('[data-agent-tokens]').textContent=completed?'20,846':Math.floor(20000+Math.min(t/15,1)*846).toLocaleString('en-US');
        ui.querySelector('[data-agent-stream]').textContent=completed?'✓ All checks passed':states[Math.floor(t/3.6)%4].slice(0,Math.floor((t%3.6)*18))+'▍';
        ui.querySelector('[data-agent-clock]').textContent='1:30:'+String(25+Math.floor(t)).padStart(2,'0');
        const model=ui.querySelector('[data-agent-model]');model.textContent=['Codex','Claude','Local'][Math.floor(t/3.6)%3];model.dataset.model=String(Math.floor(t/3.6)%3);
        ui.querySelector('[data-agent-weekly]').textContent=(75-Math.floor(Math.min(t,15)/5))+'%';
        ui.querySelector('.mxw-agent-rail').style.transform=`translateX(${-192*ramp(5.5,7,t)*(1-ramp(19,20.5,t))}px)`;
      }
    }
    const lyricsPlaying=()=>lyricsOpen&&(audio?!audio.paused:lyricWanted&&cues.length>1);
    function needsFrame(){const scene=currentScene();return available()&&((scene==='topic_6'&&aiWanted)||(scene==='topic_4'&&videoWanted)||(scene==='topic_1'&&lyricsPlaying()));}
    function tick(now){raf=0;if(!needsFrame())return;const dt=Math.min((now-(last||now))/1000,.08);last=now;
      if(currentScene()==='topic_6'&&aiWanted){elapsed+=dt;if(now-aiTick>=100){aiTick=now;drawAI(elapsed%22);}}
      if(currentScene()==='topic_4'&&videoWanted&&now-syncTick>200){syncTick=now;const a=videos[0];if(a&&!a.paused)for(const b of videos.slice(1))if(Number.isFinite(b.duration)&&Math.abs(b.currentTime-a.currentTime)>.065)b.currentTime=Math.min(a.currentTime,b.duration-.01);}
      if(currentScene()==='topic_1'){if(!audio&&lyricWanted)lyricElapsed+=dt;renderLyrics();}raf=requestAnimationFrame(tick);
    }
    function refresh(){
      const active=available(),scene=currentScene();root.classList.toggle('mxe-paused',!active);
      root.classList.toggle('mxe-ai-running',active&&scene==='topic_6'&&aiWanted);
      if(active&&scene==='topic_4'&&videoWanted)startVideos();else pauseVideos();
      if(audio&&(!active||scene!=='topic_1'))audio.pause();
      root.classList.toggle('mxe-music-playing',active&&scene==='topic_1'&&audio&&!audio.paused);
      if(lyricMotion){if(active&&scene==='topic_1'&&lyricsPlaying())lyricMotion.play();else lyricMotion.pause();}
      button(lyricButton,lyricWanted,'lyrics');if(lyricButton)lyricButton.hidden=!lyricsOpen||!cues.length||(root.querySelector('.mxe-mobile-viewer[open]')&&scene!=='topic_1');
      button(videoButton,videoWanted,'videos');button(aiButton,aiWanted,'animation');button(audioButton,audio&&!audio.paused,'music');
      if(needsFrame()&&!raf){last=0;raf=requestAnimationFrame(tick);}else if(!needsFrame()){cancelAnimationFrame(raf);raf=0;last=0;}
    }
    const io=new IntersectionObserver(entries=>{visible=entries.some(e=>e.isIntersecting);refresh();},{threshold:.08});io.observe(media);
    const observer=new MutationObserver(refresh);root.querySelectorAll('.feature-media-layer').forEach(n=>observer.observe(n,{attributes:true,attributeFilter:['class']}));
    function setMusicView(open){
      lyricsOpen=open&&cues.length>0;
      musicScreen?.classList.toggle('is-lyrics',lyricsOpen);
      if(musicToggle){musicToggle.textContent=lyricsOpen?'Show player':'Show lyrics';musicToggle.setAttribute('aria-expanded',String(lyricsOpen));}
      renderLyrics(true);refresh();
    }
    musicToggle?.addEventListener('click',()=>setMusicView(!lyricsOpen),{signal});
    root.querySelectorAll('[data-music-view]').forEach(n=>n.addEventListener('click',()=>setMusicView(n.dataset.musicView==='lyrics'),{signal}));
    videoButton?.addEventListener('click',()=>{videoWanted=!videoWanted;refresh();},{signal});
    aiButton?.addEventListener('click',()=>{aiWanted=!aiWanted;refresh();},{signal});
    lyricButton?.addEventListener('click',()=>{lyricWanted=!lyricWanted;refresh();},{signal});
    videos.forEach(v=>{v.muted=true;v.addEventListener('canplay',refresh,{signal});v.addEventListener('error',()=>{videoWanted=false;refresh();},{signal});});
    audioButton?.addEventListener('click',async()=>{if(!audio.paused)audio.pause();else try{await audio.play();}catch{audioButton.textContent='Play music';}refresh();},{signal});
    if(audio)for(const name of ['play','pause','ended','seeked','timeupdate'])audio.addEventListener(name,()=>{renderLyrics();if(name!=='timeupdate')refresh();},{signal});
    document.addEventListener('visibilitychange',refresh,{signal});reduced.addEventListener('change',()=>{aiWanted=!reduced.matches;videoWanted=!reduced.matches;lyricWanted=!reduced.matches;renderLyrics(true);refresh();},{signal});
    document.fonts?.ready.then(()=>{if(alive)renderLyrics(true);});
    // Perspective follows the original display aperture; each overlay stays a native DOM UI.
    function fitScreen(screen,corners,width,height) {
      const parent=getComputedStyle(screen.parentElement);
      const q=corners.map(([x,y])=>[x*parseFloat(parent.width)/width,y*parseFloat(parent.height)/height]);
      const plane=screenHomography(q),inset=480*.0125;
      const inside=[[inset,inset],[480-inset,inset],[480-inset,480-inset],[inset,480-inset]].map(([x,y])=>{
        const z=plane[6]*x+plane[7]*y+1;
        return [(plane[0]*x+plane[1]*y+plane[2])/z,(plane[3]*x+plane[4]*y+plane[5])/z];
      });
      projectScreen(screen,inside);
    }
    function screenHomography(q) {
      const src=[[0,0],[480,0],[480,480],[0,480]],a=[];
      for(let i=0;i<4;i++){const[x,y]=src[i],[u,v]=q[i];a.push([x,y,1,0,0,0,-u*x,-u*y,u],[0,0,0,x,y,1,-v*x,-v*y,v]);}
      for(let i=0;i<8;i++){let p=i;for(let j=i+1;j<8;j++)if(Math.abs(a[j][i])>Math.abs(a[p][i]))p=j;[a[i],a[p]]=[a[p],a[i]];const f=a[i][i];for(let k=i;k<9;k++)a[i][k]/=f;for(let j=0;j<8;j++)if(j!==i){const d=a[j][i];for(let k=i;k<9;k++)a[j][k]-=d*a[i][k];}}
      return a.map(r=>r[8]);
    }
    function projectScreen(screen,q) {
      const h=screenHomography(q),matrix=`matrix3d(${h[0]},${h[3]},0,${h[6]},${h[1]},${h[4]},0,${h[7]},0,0,1,0,${h[2]},${h[5]},0,1)`;
      screen.style.transform=matrix;screen.style.setProperty('--screen-perspective',matrix);screen.style.setProperty('--music-perspective',matrix);
    }
    instances.set(root,()=>{alive=false;controller.abort();io.disconnect();ro.disconnect();observer.disconnect();cancelAnimationFrame(raf);lyricMotion?.cancel();pauseVideos();audio?.pause();root.classList.remove('mxe-ai-running','mxe-music-playing');instances.delete(root);});
    setMusicView(false);root.dispatchEvent(new CustomEvent('mx:experience-ready'));refresh();
  }
  function scan(scope=document){scope.querySelectorAll?.('.experience-svg').forEach(mount);if(scope.matches?.('.experience-svg'))mount(scope);}
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',()=>scan(),{once:true});else scan();
  document.addEventListener('shopify:section:load',e=>scan(e.target));
  document.addEventListener('shopify:section:unload',e=>{for(const[root,cleanup]of instances)if(e.target===root||e.target.contains(root))cleanup();});
})();
